import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button onClick={() => navigate('/')} className="back-button">
          <ArrowLeft size={20} /> Back to Home
        </button>
        
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last Updated: February 2026</p>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Bizenntrum ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect personal information that you provide to us, including:</p>
            <ul>
              <li>Name and contact information (email address, phone number)</li>
              <li>Company name and business information</li>
              <li>Communication preferences</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you visit our website, we may automatically collect:</p>
            <ul>
              <li>IP address and browser type</li>
              <li>Device information</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and improve our consulting services</li>
              <li>Respond to your inquiries and consultation requests</li>
              <li>Send you relevant updates and newsletters (with your consent)</li>
              <li>Analyze website usage and improve user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Information Sharing and Disclosure</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share your information with:
            </p>
            <ul>
              <li>Our strategic partners for service delivery (with your consent)</li>
              <li>Service providers who assist in our operations</li>
              <li>Legal authorities when required by law</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information. However, 
              no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of marketing communications</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. International Data Transfers</h2>
            <p>
              As we operate across India and Singapore, your information may be transferred to and processed in countries 
              other than your own. We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar technologies to enhance your browsing experience. You can control cookie preferences 
              through your browser settings.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Marketing Attribution &amp; UTM Tracking</h2>
            <p>
              When you arrive on our website through a tagged campaign link (for example, a link we share on LinkedIn,
              WhatsApp, email or in an outreach message), we capture the standard campaign parameters attached to that link —
              <em> utm_source, utm_medium, utm_campaign, utm_content</em> and <em>utm_term</em> — along with the landing page URL,
              the first page visited during your session, and the date and time.
            </p>
            <p>
              These values are stored in your browser's <strong>sessionStorage</strong> for the duration of your current
              visit and in <strong>localStorage</strong> for up to 30 days so that we can preserve first-touch attribution
              even if you return later without a tagged link. If you subsequently arrive via a new tagged link,
              your current-visit attribution is updated to reflect it.
            </p>
            <p>
              If, and only if, you voluntarily submit an enquiry form or request a lead resource, the captured attribution
              values are sent to us together with the details you enter, and stored alongside your enquiry record.
              We use this information solely to understand which channels and campaigns bring us conversations, and to
              improve the relevance of our outreach.
            </p>
            <p>
              You can clear this information at any time by clearing your browser's site data for bizenntrum.com,
              or by using your browser's privacy / incognito mode.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The updated version will be indicated by an updated 
              "Last Updated" date at the top of this page.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> opsdesk@bizenntrum.com<br />
              <strong>WhatsApp:</strong> +65 8017 9673
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
